import { useEffect, useRef, useState } from "react";

interface SimulatedPlayerProps {
  cover: string;
  title: string;
  duration: string;
}

function durationToSeconds(duration: string): number {
  const [min, sec] = duration.split(":").map(Number);
  return (min || 0) * 60 + (sec || 0);
}

function formatSeconds(total: number): string {
  const min = Math.floor(total / 60);
  const sec = Math.floor(total % 60);
  return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

export default function SimulatedPlayer({ cover, title, duration }: SimulatedPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const total = durationToSeconds(duration);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    setPlaying(false);
    setElapsed(0);
  }, [title]);

  useEffect(() => {
    if (!playing) {
      if (timerRef.current) window.clearInterval(timerRef.current);
      return;
    }
    timerRef.current = window.setInterval(() => {
      setElapsed((current) => {
        if (current >= total) {
          setPlaying(false);
          return total;
        }
        return current + 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [playing, total]);

  const progress = total > 0 ? Math.min(100, (elapsed / total) * 100) : 0;

  const togglePlay = () => {
    if (elapsed >= total) {
      setElapsed(0);
      setPlaying(true);
      return;
    }
    setPlaying((value) => !value);
  };

  return (
    <div className="w-full">
      <div className="group relative w-full overflow-hidden rounded-lg bg-foreground-950">
        <div className="relative aspect-video w-full">
          <img
            src={cover}
            alt={`${title} 播放封面`}
            title={`${title} - 91短视频`}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-foreground-950/45"></div>

          <button
            type="button"
            onClick={togglePlay}
            aria-label={playing ? "暂停" : "播放"}
            className="absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-3"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50 transition group-hover:scale-110">
              <i className={`${playing ? "ri-pause-fill" : "ri-play-fill"} text-3xl`}></i>
            </span>
            <span className="rounded-full bg-foreground-950/60 px-3 py-1 text-xs text-background-100">
              {playing ? "演示播放中（无真实视频流）" : "点击播放（演示效果，无真实视频流）"}
            </span>
          </button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-foreground-950/85 to-transparent px-4 pb-3 pt-8">
          <div className="mb-2 h-1 w-full overflow-hidden rounded-full bg-background-50/25">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-1000"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="flex items-center justify-between text-xs text-background-100">
            <span>{formatSeconds(elapsed)}</span>
            <span className="flex items-center gap-3">
              <i className="ri-volume-up-line"></i>
              <i className="ri-fullscreen-line"></i>
              {formatSeconds(total)}
            </span>
          </div>
        </div>
      </div>

      <p className="mt-2 flex items-center gap-1.5 text-xs text-foreground-400">
        <i className="ri-information-line"></i>
        温馨提示：本页播放器为演示占位，仅模拟播放进度，不接入真实视频流。
      </p>
    </div>
  );
}